/**
 * RED-317 PR 3d-A — which chart of an editor is being edited, so the canvas
 * (OverviewCanvas) can mount the inline editor (StudioChartEditor) into it.
 * The chart's node view registers a controller for its DOM; a session is
 * at most one chart per editor:
 *
 * The schema stays free of Vue: state is an `observable` cell the canvas
 * mirrors into a ref (composables/useChartEditSession.js).
 *
 *   controller = {
 *     state: Observable<{ node, pos }>,      the node as the editor holds it now
 *     model(): the server's chart model (presentation payload), or null
 *     editable(): whether this chart can be edited on the canvas
 *     pos(): number | null                   the block's position now
 *     open(): HTMLElement                    the host the editor mounts into (below the chart)
 *     close()
 *     preview(edit | undefined)              draw with a not yet committed override (undefined: the node's own)
 *     onRender(callback) → unsubscribe       after every redraw (overlays re-measure)
 *     block(): HTMLElement                   the chart block (positioned; overlays measure against it)
 *   }
 */

/** A value cell with change listeners: `.value`, `.subscribe(fn) → unsubscribe`. */
export function observable(initial) {
  let value = initial
  const listeners = new Set()
  return {
    get value() {
      return value
    },
    set value(next) {
      value = next
      for (const listener of [...listeners]) listener(next)
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

const controllers = new WeakMap()
const sessions = new WeakMap()

export function registerChartView(dom, controller) {
  controllers.set(dom, controller)
}

export function unregisterChartView(dom) {
  controllers.delete(dom)
}

/** The editor's session ref: null, or { controller }. */
export function chartEditSession(editor) {
  if (!sessions.has(editor)) sessions.set(editor, observable(null))
  return sessions.get(editor)
}

/** The controller of the chart block at `pos`, or null. */
export function chartControllerAt(editor, pos) {
  if (!editor?.view || typeof pos !== 'number') return null
  const dom = editor.view.nodeDOM(pos)
  return dom ? (controllers.get(dom) ?? null) : null
}

/** Whether the chart at `pos` can be edited on the canvas. */
export function canEditChart(editor, pos) {
  return Boolean(editor?.isEditable && chartControllerAt(editor, pos)?.editable())
}

/** Open the inline editor on the chart at `pos`. */
export function startChartEdit(editor, pos) {
  const controller = chartControllerAt(editor, pos)
  if (!editor?.isEditable || !controller?.editable()) return false
  const session = chartEditSession(editor)
  if (session.value?.controller === controller) return true
  session.value?.controller.close()
  controller.open()
  session.value = { controller }
  return true
}

export function stopChartEdit(editor) {
  const session = chartEditSession(editor)
  if (!session.value) return
  session.value.controller.close()
  session.value = null
}

/** A node view going away ends its session. */
export function endSessionOf(editor, controller) {
  const session = sessions.get(editor)
  if (session?.value?.controller === controller) session.value = null
}

export function isEditingChart(editor) {
  return Boolean(sessions.get(editor)?.value)
}
